// OVO-113 — Recurring orders tab on PartnerDetail.
//
// A flat table of every schedule for the partner. Replaces the
// per-Site expand-row pattern with a simpler "all in one place" view:
// pickup site, delivery site, days, window, status, and per-row
// edit / delete / cancel.
//
// "Run materialization now" button in the toolbar lets the operator
// fire the cron's job for a given date on demand (useful for testing,
// backfilling missed days, or kicking the system after a manual fix).
//
// Admin-only — partner viewers can cancel individual materialized
// orders from the dashboard but cannot edit schedules themselves.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {
  HistoryOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  SyncOutlined,
  CompassOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ScheduleEditor from '../components/ScheduleEditor';
import DiscoverSchedulesModal from '../components/DiscoverSchedulesModal';
import MaterializationHistoryModal from '../components/MaterializationHistoryModal';
import TableFilterDrawer, { TableFilterButton, countActiveFilters } from '../components/TableFilterDrawer';
import useColumnConfig from '../hooks/useColumnConfig';
import ColumnConfigDrawer from '../components/ColumnConfigDrawer';
import { ReactSVG } from 'react-svg';
import RecurringWeeklyGrid from '../components/RecurringWeeklyGrid';
import {
  listSchedulesForPartner,
  deleteSchedule,
  materializeSchedules,
  regenerateRoutesWindow,
} from '../modules/order-schedules/api';
import { updatePartner, getPartnerItemTypes } from '../modules/partners/api';
import { boxLabel, byPosition } from '../utils/scheduleBox';

// OVO-131 — per-partner materialization horizon presets (days ahead).
const HORIZON_OPTIONS = [
  { value: 1, label: 'Next day (J+1)' },
  { value: 2, label: '2 days ahead (J+2)' },
  { value: 3, label: '3 days ahead (J+3)' },
  { value: 7, label: 'Next week (7 days)' },
  { value: 14, label: '2 weeks (14 days)' },
];

const DAY_LABELS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

function WeekdayDisplay({ weekdays = [] }) {
  return (
    <div style={{ display: 'flex', gap: 3 }}>
      {DAY_LABELS.map((label, i) => (
        <span
          key={i}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 600,
            background: weekdays[i] ? 'var(--accent-strong)' : '#f0f0f0',
            color: weekdays[i] ? '#fff' : '#bbb',
          }}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

export default function PartnerSchedules({ partner }) {
  const { t: tPartners } = useTranslation('partners');
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  // View mode + pickup/delivery filter are URL-synced so a specific view is a
  // shareable/bookmarkable link (e.g. ?view=byday&show=pickup for a
  // pickups-only-by-day view per customer). Params are merged (partnerId on the
  // global /admin/recurring page is preserved).
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'byday' ? 'byday' : 'list';
  const showParam = searchParams.get('show'); // null = both | 'pickup' | 'delivery' | 'none'
  const showPickups = showParam !== 'delivery' && showParam !== 'none';
  const showDeliveries = showParam !== 'pickup' && showParam !== 'none';
  const mergeParams = (mut) =>
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        mut(p);
        return p;
      },
      { replace: true },
    );
  const setView = (v) => mergeParams((p) => (v === 'byday' ? p.set('view', 'byday') : p.delete('view')));
  const setShow = (pick, deliv) =>
    mergeParams((p) => {
      const val = pick && deliv ? null : pick ? 'pickup' : deliv ? 'delivery' : 'none';
      if (val) p.set('show', val);
      else p.delete('show');
    });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // OVO-147 — id → item-type lookup so the Products column can render
  // names ("2× OM") from the schedule's defaultProducts JSON (ids only).
  const [itemTypesById, setItemTypesById] = useState({});
  useEffect(() => {
    if (!partner?.id) return;
    getPartnerItemTypes(partner.id).then(({ response }) => {
      const list = response?.partnerItemTypes || response?.items || (Array.isArray(response) ? response : []);
      setItemTypesById(Object.fromEntries(list.map((t) => [t.id, t])));
    });
  }, [partner?.id]);

  // "Run materialization now" modal state — the operator picks a target
  // date (defaults to tomorrow) and fires the job. Same endpoint the
  // 06:00 UTC cron calls.
  const [matModalOpen, setMatModalOpen] = useState(false);
  const [matDate, setMatDate] = useState(() => dayjs().add(1, 'day'));
  const [matRunning, setMatRunning] = useState(false);
  const [matResult, setMatResult] = useState(null);

  // OVO-131 — per-partner materialization horizon (days ahead). Seeded
  // from the partner, persisted via PATCH /partners/:id.
  const [horizon, setHorizon] = useState(partner?.materializationHorizonDays ?? 1);
  const [savingHorizon, setSavingHorizon] = useState(false);
  useEffect(() => {
    setHorizon(partner?.materializationHorizonDays ?? 1);
  }, [partner?.materializationHorizonDays]);

  // OVO-141 — cancellation cutoff (hours before delivery) past which a
  // partner viewer can no longer cancel a materialized recurring order.
  const [cutoff, setCutoff] = useState(partner?.scheduleEditCutoffHoursBefore ?? 18);
  const [savingCutoff, setSavingCutoff] = useState(false);
  useEffect(() => {
    setCutoff(partner?.scheduleEditCutoffHoursBefore ?? 18);
  }, [partner?.scheduleEditCutoffHoursBefore]);

  const saveCutoff = async (value) => {
    if (value == null || Number.isNaN(value)) return;
    const prev = cutoff;
    setCutoff(value); // optimistic
    setSavingCutoff(true);
    try {
      const { error } = await updatePartner({ id: partner.id, scheduleEditCutoffHoursBefore: value });
      if (error) {
        setCutoff(prev);
        message.error(error.message || 'Could not update the cutoff');
      } else {
        message.success('Cancellation cutoff updated');
      }
    } catch (e) {
      setCutoff(prev);
      message.error(e?.message || 'Could not update the cutoff');
    } finally {
      setSavingCutoff(false);
    }
  };

  const saveHorizon = async (value) => {
    const prev = horizon;
    setHorizon(value); // optimistic
    setSavingHorizon(true);
    try {
      const { error } = await updatePartner({ id: partner.id, materializationHorizonDays: value });
      if (error) {
        setHorizon(prev);
        message.error(error.message || 'Could not update the horizon');
      } else {
        message.success('Materialization horizon updated');
      }
    } catch (e) {
      setHorizon(prev);
      message.error(e?.message || 'Could not update the horizon');
    } finally {
      setSavingHorizon(false);
    }
  };

  // OVO-113 — "Discover schedules" modal: scans recent orders and
  // proposes recurring patterns. Operator edits + bulk-saves a subset.
  const [discoverOpen, setDiscoverOpen] = useState(false);
  // The nightly job's log has been recorded since OVO-113 but never shown.
  const [historyOpen, setHistoryOpen] = useState(false);
  // Filter drawer (same UX as Orders). Client-side: the page already holds
  // every schedule of the partner.
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});

  const reload = useCallback(async () => {
    if (!partner?.id) return;
    setLoading(true);
    try {
      const { response } = await listSchedulesForPartner(partner.id);
      setSchedules(Array.isArray(response) ? response : []);
    } catch (e) {
      message.error(e?.message || 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  }, [partner?.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleDelete = async (id) => {
    const { error } = await deleteSchedule(id);
    if (error) {
      message.error(error.message || 'Failed to delete');
      return;
    }
    message.success('Schedule deleted');
    reload();
    if (fixedTours) runRegenerate({ silent: true });
  };

  // #83 — after a schedule changes, the already-generated days of the rolling
  // window are rebuilt on demand (the nightly cron would do it anyway).
  const fixedTours = partner?.routeGenerationMode === 'FIXED_TOURS';
  const [regenRunning, setRegenRunning] = useState(false);
  const [regenResult, setRegenResult] = useState(null);
  const runRegenerate = async ({ silent = false } = {}) => {
    if (!partner?.id) return;
    setRegenRunning(true);
    try {
      const { response, error } = await regenerateRoutesWindow(partner.id);
      if (error) {
        message.error(error.message || 'Regeneration failed');
        return;
      }
      setRegenResult(response);
      const t = response?.totals || {};
      const summary = `${response?.dates?.length || 0} day(s): ${t.ordersCreated || 0} order(s) created, ${t.created || 0} route(s) created, ${t.rebuilt || 0} rebuilt, ${t.locked || 0} left as sent`;
      if (!silent) message.success(`Routes regenerated — ${summary}`, 6);
      else message.info(`Recurring change applied to the next ${response?.dates?.length || 0} day(s) — ${summary}`, 6);
      reload();
    } catch (e) {
      message.error(e?.message || 'Regeneration failed');
    } finally {
      setRegenRunning(false);
    }
  };

  const runMaterialization = async () => {
    setMatRunning(true);
    setMatResult(null);
    try {
      const { response, error } = await materializeSchedules(partner.id, {
        date: matDate.format('YYYY-MM-DD'),
      });
      if (error) {
        message.error(error.message || 'Materialization failed');
        setMatRunning(false);
        return;
      }
      setMatResult(response);
      reload(); // schedule _count.orders is now stale
    } catch (e) {
      message.error(e?.message || 'Materialization failed');
    } finally {
      setMatRunning(false);
    }
  };

  // Sort schedules: active first, then by created date desc.
  const sortedSchedules = useMemo(
    () =>
      [...schedules].sort((a, b) => {
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }),
    [schedules],
  );

  // Options for the filter drawer, derived from the loaded schedules so only
  // sites that actually appear in the list are offered.
  const filterSiteOptions = useMemo(() => {
    const bySide = (key) => {
      const seen = new Map();
      for (const r of schedules) {
        const site = r[key];
        if (site?.id && !seen.has(site.id)) seen.set(site.id, site.name);
      }
      return [...seen.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label));
    };
    // #169 — the tournée tags (types), to read one tournée in Position order.
    const types = [...new Set(schedules.flatMap((r) => r.defaultTypes || []))]
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
    return { pickup: bySide('pickupSite'), delivery: bySide('deliverySite'), types };
  }, [schedules]);

  const filteredSchedules = useMemo(
    () =>
      sortedSchedules.filter((r) => {
        if (filters.pickupSiteId && r.pickupSite?.id !== filters.pickupSiteId) return false;
        if (filters.deliverySiteId && r.deliverySite?.id !== filters.deliverySiteId) return false;
        if (filters.type && !(r.defaultTypes || []).includes(filters.type)) return false;
        if (filters.status) {
          // Mirrors the Status column: inactive wins, then a pause window.
          const status = !r.isActive ? 'inactive' : r.pausedFrom ? 'paused' : 'active';
          if (status !== filters.status) return false;
        }
        if (filters.weekday !== undefined && filters.weekday !== null && !r.weekdays?.[filters.weekday]) return false;
        return true;
      }),
    [sortedSchedules, filters],
  );

  // Same table chrome as every other list in the planner: the gear opens a
  // Settings drawer owning column visibility/order AND the page size.
  // Previously this table hard-coded `pagination={{ pageSize: 30 }}`, which
  // AntD treats as a CONTROLLED page size — so the bottom "N / page" selector
  // appeared but every choice was overwritten back to 30 on the next render.
  const FIXED_KEYS = ['actions'];

  const columns = [
    {
      title: 'Pickup site',
      key: 'pickupSite',
      render: (_, r) =>
        r.pickupSite ? (
          <Tag color="cyan" style={{ fontSize: 11 }}>
            {r.pickupSite.name}
          </Tag>
        ) : (
          <span style={{ color: '#bbb', fontSize: 12, fontStyle: 'italic' }}>(tour-mapped)</span>
        ),
    },
    {
      // #170 — the box the task collects (a site can hold several).
      title: tPartners('recurring.box'),
      key: 'box',
      render: (_, r) =>
        r.collectionPoint ? (
          <Tag color={r.collectionPoint.isArchived ? 'default' : 'gold'} style={{ fontSize: 11 }}>
            {boxLabel(r.collectionPoint)}
          </Tag>
        ) : (
          <span style={{ color: '#bbb' }}>—</span>
        ),
    },
    // #169 — stop order inside the tournée. Always declared so the column
    // config keeps its place (the partner arrives after the first render);
    // the table shows it for FIXED_TOURS partners only (`shownColumns` — the
    // drawer keeps the full list, its move indices must match the config).
    {
      title: <Tooltip title={tPartners('recurring.positionColumnTooltip')}>{tPartners('recurring.position')}</Tooltip>,
      configLabel: tPartners('recurring.position'),
      key: 'position',
      width: 160,
      sorter: byPosition,
      render: (_, r) => (
        <Space size={4} wrap>
          <strong>{r.position ?? '—'}</strong>
          {(r.defaultTypes || []).map((tag) => (
            <Tag key={tag} style={{ fontSize: 11, marginInlineEnd: 0 }}>
              {tag}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Delivery site',
      key: 'deliverySite',
      render: (_, r) =>
        r.deliverySite ? (
          <Tag color="geekblue" style={{ fontSize: 11 }}>
            {r.deliverySite.name}
          </Tag>
        ) : (
          <span style={{ color: '#bbb', fontSize: 12, fontStyle: 'italic' }}>(tour-mapped)</span>
        ),
    },
    {
      title: 'Days',
      key: 'weekdays',
      width: 200,
      render: (_, r) => <WeekdayDisplay weekdays={r.weekdays} />,
    },
    {
      title: 'Window',
      key: 'window',
      width: 110,
      render: (_, r) => {
        // Render a partial window too (start-only / end-only), not just both.
        const s = r.deliveryTimeStart;
        const e = r.deliveryTimeEnd;
        if (s && e) return `${s}–${e}`;
        if (s) return `${s}–…`;
        if (e) return `…–${e}`;
        return '—';
      },
    },
    {
      // OVO-147 — compact summary of the schedule's default products.
      // "driver weighs" marks a requires-weight type with no pre-filled
      // weight (the pickup-weight-enrichment convention).
      title: 'Products',
      key: 'products',
      render: (_, r) => {
        const products = Array.isArray(r.defaultProducts) ? r.defaultProducts : [];
        if (products.length === 0) return <span style={{ color: '#bbb' }}>—</span>;
        return (
          <span>
            {products.map((p, i) => {
              const t = itemTypesById[p.partnerItemTypeId];
              const name = t?.name || `#${p.partnerItemTypeId}`;
              const weight =
                p.weight != null
                  ? ` · ${p.weight}${t?.unitOfMeasure || 'kg'}`
                  : t?.requiresWeight
                    ? ' · driver weighs'
                    : '';
              return (
                <Tag key={i} color="purple" style={{ fontSize: 11, marginBottom: 2 }}>
                  {p.quantity || 1}× {name}
                  {weight}
                </Tag>
              );
            })}
          </span>
        );
      },
    },
    {
      title: 'Date range',
      key: 'range',
      render: (_, r) => {
        const start = r.startDate ? dayjs(r.startDate).format('DD MMM YYYY') : '?';
        const end = r.endDate ? dayjs(r.endDate).format('DD MMM YYYY') : 'no end';
        return (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {start} → {end}
          </span>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_, r) => {
        if (!r.isActive) return <Tag>inactive</Tag>;
        if (r.pausedFrom) {
          const from = dayjs(r.pausedFrom).format('DD MMM');
          const until = r.pausedUntil ? dayjs(r.pausedUntil).format('DD MMM') : '?';
          return (
            <Tag color="orange">
              paused {from}–{until}
            </Tag>
          );
        }
        return <Tag color="green">active</Tag>;
      },
    },
    {
      title: 'Materialized',
      key: 'materialized',
      width: 100,
      align: 'right',
      render: (_, r) => r._count?.orders ?? 0,
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_, r) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditing(r);
              setEditorOpen(true);
            }}
          />
          <Popconfirm
            title="Delete this schedule?"
            description="Already-materialized orders survive; only future occurrences stop."
            onConfirm={() => handleDelete(r.id)}
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const columnConfig = useColumnConfig('recurring-columns', columns, FIXED_KEYS);
  const shownColumns = (list) => (fixedTours ? list : list.filter((c) => c.key !== 'position'));

  return (
    <div className="partner-schedules">
      <Card title="Recurring orders" size="small">
        {/* Toolbar moved out of the Card `extra` into the body — the crowded
            extra (Horizon + cutoff + 3 buttons) was clipping the title. */}
        <div
          style={{
            marginBottom: 12,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <Tooltip title="How many days ahead the nightly job creates this partner's recurring orders. Larger = next week is built in advance, not the eve.">
            <Space size={4}>
              <CalendarOutlined style={{ color: 'var(--text-muted)' }} />
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Horizon</span>
              <Select
                size="small"
                value={horizon}
                loading={savingHorizon}
                onChange={saveHorizon}
                options={HORIZON_OPTIONS}
                style={{ width: 180 }}
              />
            </Space>
          </Tooltip>
          <Tooltip title="How many hours before the delivery date a partner viewer can still cancel a materialized recurring order. Past this cutoff the cancel button is disabled for them (admins can still force-cancel). 0 = allow up to the delivery day.">
            <Space size={4}>
              <ClockCircleOutlined style={{ color: 'var(--text-muted)' }} />
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Cancel cutoff</span>
              <InputNumber
                size="small"
                min={0}
                max={336}
                value={cutoff}
                disabled={savingCutoff}
                onChange={(v) => setCutoff(v)}
                onBlur={() => saveCutoff(cutoff)}
                onPressEnter={() => saveCutoff(cutoff)}
                addonAfter="h"
                style={{ width: 96 }}
              />
            </Space>
          </Tooltip>
          <Button icon={<CompassOutlined />} onClick={() => setDiscoverOpen(true)}>
            Discover schedules
          </Button>
          <Button icon={<PlayCircleOutlined />} onClick={() => setMatModalOpen(true)}>
            Run materialization now
          </Button>
          {fixedTours && (
            <Tooltip title="Re-create the recurring orders and rebuild the generated routes for the next days (the partner's horizon). Routes already sent to a driver are left as they are.">
              <Button icon={<SyncOutlined />} loading={regenRunning} onClick={() => runRegenerate()}>
                Regenerate routes now
              </Button>
            </Tooltip>
          )}
          <Button icon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)}>
            History
          </Button>
          <TableFilterButton activeCount={countActiveFilters(filters)} onClick={() => setFilterOpen(true)} />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setEditorOpen(true);
            }}
          >
            Add schedule
          </Button>
          {/* Same markup as the Orders toolbar gear: text button with the SVG
              as a CHILD (not the `icon` prop, which makes AntD size it as an
              icon-only button and drops the glyph out of its box). */}
          <Button
            type="text"
            title="Settings"
            onClick={columnConfig.openDrawer}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <ReactSVG className="icon" src="/icons/settings.svg" />
          </Button>
        </div>
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <Segmented
            size="small"
            value={view}
            onChange={setView}
            options={[
              { label: 'List', value: 'list', icon: <UnorderedListOutlined /> },
              { label: 'By day', value: 'byday', icon: <CalendarOutlined /> },
            ]}
          />
          {view === 'byday' && (
            <Space size={4}>
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Show</span>
              <Tag.CheckableTag checked={showPickups} onChange={(c) => setShow(c, showDeliveries)}>
                Pickups
              </Tag.CheckableTag>
              <Tag.CheckableTag checked={showDeliveries} onChange={(c) => setShow(showPickups, c)}>
                Deliveries
              </Tag.CheckableTag>
            </Space>
          )}
        </div>
        {view === 'byday' ? (
          // OVO — weekly grid: which sites are visited each weekday (recurring pattern).
          <RecurringWeeklyGrid schedules={sortedSchedules} showPickups={showPickups} showDeliveries={showDeliveries} />
        ) : (
          <>
            {regenResult && (
              <Alert
                type={regenResult.totals?.skipped ? 'warning' : 'success'}
                showIcon
                closable
                onClose={() => setRegenResult(null)}
                style={{ marginBottom: 12 }}
                message={`Regenerated ${regenResult.dates?.[0]} → ${regenResult.dates?.[regenResult.dates.length - 1]}: ${regenResult.totals?.ordersCreated || 0} order(s) created · ${regenResult.totals?.created || 0} route(s) created · ${regenResult.totals?.rebuilt || 0} rebuilt · ${regenResult.totals?.locked || 0} left as sent · ${regenResult.totals?.skipped || 0} skipped`}
                description={
                  regenResult.days?.some((d) =>
                    d.outcomes?.some((o) => o.status === 'SKIPPED' || o.warnings?.length),
                  ) ? (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {regenResult.days.flatMap((d) =>
                        (d.outcomes || [])
                          .filter((o) => o.status === 'SKIPPED' || o.warnings?.length)
                          .map((o) => (
                            <li key={`${d.date}-${o.tag}`}>
                              {d.date} · {o.tag} ({o.driver || '—'}):{' '}
                              {o.status === 'SKIPPED' ? o.reason : o.warnings.join('; ')}
                            </li>
                          )),
                      )}
                    </ul>
                  ) : null
                }
              />
            )}
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Materialization runs nightly at 06:00 UTC"
              description={
                <span>
                  Each night the job creates this partner&apos;s recurring orders for the next{' '}
                  <strong>{horizon === 1 ? 'day' : `${horizon} days`}</strong> (the <strong>Horizon</strong> above) —
                  every active schedule whose weekday matches a day in that window produces one Order. Re-runs are safe
                  (already-created days are skipped). You can also trigger a specific date via{' '}
                  <strong>Run materialization now</strong>. If you leave a side blank (Pickup OR Delivery), the
                  partner&apos;s tour mapping fills it at order-create time.
                </span>
              }
            />
            <Table
              dataSource={filteredSchedules}
              columns={shownColumns(columnConfig.visibleColumns)}
              rowKey="id"
              size="small"
              loading={loading}
              scroll={{ x: 'max-content' }}
              pagination={{
                pageSize: columnConfig.pageSize,
                showSizeChanger: false,
                hideOnSinglePage: true,
              }}
              locale={{ emptyText: 'No recurring schedules yet. Click "Add schedule" to create one.' }}
            />
            <ColumnConfigDrawer
              open={columnConfig.drawerOpen}
              onClose={columnConfig.closeDrawer}
              columns={columnConfig.orderedAllColumns}
              hidden={columnConfig.hidden}
              fixedKeys={FIXED_KEYS}
              onToggle={columnConfig.toggleColumn}
              onMove={columnConfig.moveColumn}
              onReset={columnConfig.resetConfig}
              pageSize={columnConfig.pageSize}
              onPageSizeChange={columnConfig.setPageSize}
            />
          </>
        )}
      </Card>

      <DiscoverSchedulesModal
        partnerId={partner?.id}
        open={discoverOpen}
        onClose={() => setDiscoverOpen(false)}
        onSaved={() => {
          setDiscoverOpen(false);
          reload();
          if (fixedTours) runRegenerate({ silent: true });
        }}
      />
      <TableFilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        values={filters}
        onApply={setFilters}
        fields={[
          { name: 'pickupSiteId', label: 'Pickup site', options: filterSiteOptions.pickup, placeholder: 'Any' },
          { name: 'deliverySiteId', label: 'Delivery site', options: filterSiteOptions.delivery, placeholder: 'Any' },
          {
            name: 'type',
            label: tPartners('recurring.tourFilter'),
            options: filterSiteOptions.types,
            placeholder: 'Any',
          },
          {
            name: 'status',
            label: 'Status',
            placeholder: 'Any',
            options: [
              { value: 'active', label: 'Active' },
              { value: 'paused', label: 'Paused' },
              { value: 'inactive', label: 'Inactive' },
            ],
          },
          {
            name: 'weekday',
            label: 'Runs on day',
            placeholder: 'Any',
            options: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(
              (label, value) => ({ value, label }),
            ),
          },
        ]}
      />
      <MaterializationHistoryModal open={historyOpen} partnerId={partner?.id} onClose={() => setHistoryOpen(false)} />

      <ScheduleEditor
        open={editorOpen}
        partnerId={partner?.id}
        site={null}
        defaultRole="delivery"
        editing={editing}
        showPosition={fixedTours}
        onClose={() => {
          setEditorOpen(false);
          setEditing(null);
        }}
        onSaved={() => {
          setEditorOpen(false);
          setEditing(null);
          reload();
          if (fixedTours) runRegenerate({ silent: true });
        }}
      />

      {/* Run-materialization modal. The operator picks a date, fires
          the same endpoint the nightly cron uses, then sees a per-
          schedule summary of what happened. */}
      <Modal
        open={matModalOpen}
        title="Run materialization"
        onCancel={() => {
          setMatModalOpen(false);
          setMatResult(null);
        }}
        onOk={runMaterialization}
        okText="Run"
        okButtonProps={{ loading: matRunning }}
        width={620}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Pick the target delivery date"
          description="The job will create one Order per matching schedule for the date below. Re-running for the same date is safe — duplicates are skipped via the (schedule, deliveryDate) unique index."
        />
        <div style={{ marginBottom: 12 }}>
          <strong>Target delivery date:</strong>
          <DatePicker value={matDate} onChange={(d) => setMatDate(d)} format="YYYY-MM-DD" style={{ marginLeft: 12 }} />
        </div>
        {matResult && (
          <div style={{ background: 'var(--surface-2)', padding: 12, borderRadius: 4 }}>
            <div style={{ marginBottom: 8 }}>
              <strong>Result for {matResult.targetDate}:</strong>
            </div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
              <Tag color="green">{matResult.created} created</Tag>
              <Tag color="orange">{matResult.skipped} skipped</Tag>
              <Tag color="red">{matResult.failed} failed</Tag>
              <span style={{ color: 'var(--text-secondary)' }}>· {matResult.processed} schedules processed</span>
            </div>
            {matResult.perScheduleResults?.length > 0 && (
              <div style={{ fontSize: 12, color: '#555', maxHeight: 200, overflowY: 'auto' }}>
                {matResult.perScheduleResults.map((r) => (
                  <div key={r.scheduleId}>
                    Schedule #{r.scheduleId}: <strong>{r.status}</strong>
                    {r.orderId && ` → Order #${r.orderId}`}
                    {r.reason && <span style={{ color: 'var(--text-muted)' }}> — {r.reason}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
